import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// import { API_URL } from '../../../../Constant';
import { API_URL } from '../../../../../Constant';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
// import messages from '../../../../assets/Messages.json';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';

function BalanceSheetDataCheck() {
  const navigate = useNavigate();

  // State for API data
  const [blockDData, setBlockDData] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiCallMadeRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  // API configuration
  const getAuthConfig = () => {
    let token = localStorage.getItem('jwt') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('userToken');

    if (!token) {
      console.error('No token found in localStorage');
      return null;
    }

    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    };
  };

  // Fetch Block D data
  const fetchBlockDData = useCallback(async () => {
    // Prevent multiple API calls using refs
    if (apiCallMadeRef.current || isLoadingRef.current) {
      console.log('API call already in progress or completed');
      return;
    }

    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      setLoading(false);
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0] // Extract year from format like "2022-23"
    };

    // Check if we already called API for this exact DSL and Year
    const callKey = `${blockInfo.dslNo}-${blockInfo.surveyYear}`;
    if (apiCallMadeRef.current === callKey) {
      console.log('API already called for this DSL and Year combination');
      return;
    }

    apiCallMadeRef.current = callKey;
    isLoadingRef.current = true;
    showLoading(true);
    setLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        setLoading(false);
        isLoadingRef.current = false;
        apiCallMadeRef.current = false;
        return;
      }

      console.log('Fetching Block D data with payload:', requestPayload);
      const response = await axios.post(`${API_URL}api/blockd/get-data`, requestPayload, config);
      const { data, status } = response.data;

      if (status && data && data.length > 0) {
        setBlockDData(data);
        console.log('Block D data loaded successfully:', data.length, 'items');
      } else {
        setBlockDData([]);
        toast.warning('No Block D data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockDData([]);
    } finally {
      showLoading(false);
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Load data on component mount - only once
  useEffect(() => {
    // Only call if we have required data and haven't called yet
    if (blockInfo.dslNo && blockInfo.surveyYear && !apiCallMadeRef.current) {
      fetchBlockDData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <style>
        {`
          .balance-sheet-header {
            background-color: #e3f2fd;
            padding: 15px 20px;
            border-bottom: 2px solid #1976d2;
            margin-bottom: 20px;
          }
          .page-title {
            color: #1976d2;
            font-size: 24px;
            font-weight: bold;
            margin: 0;
          }
          .dispatch-number {
            color: #333;
            font-size: 16px;
            font-weight: 500;
            margin: 0;
          }
          .table th {
            background-color: #1976d2;
            color: #ffffff;
            font-weight: bold;
            font-size: 11px;
            text-align: center;
            vertical-align: middle;
            padding: 12px 8px;
            border: 1px solid #1565c0;
            white-space: nowrap;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .table td {
            border: 1px solid #dee2e6;
            padding: 10px 8px;
            font-size: 11px;
            text-align: right;
            vertical-align: middle;
            font-weight: 500;
          }
          .table tbody tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          .table tbody tr:nth-child(odd) {
            background-color: #ffffff;
          }
          .table tbody tr:hover {
            background-color: #e3f2fd !important;
            transition: background-color 0.2s ease;
          }
          .item-name {
            text-align: left;
            font-weight: 600;
            color: #2c3e50;
            max-width: 300px;
            word-wrap: break-word;
            line-height: 1.3;
          }
          .block-cell {
            text-align: center !important;
            font-weight: 600;
            color: #1976d2;
            font-size: 12px;
            vertical-align: middle;
          }
          .item-code {
            text-align: center !important;
            font-weight: 600;
            color: #666;
            font-size: 12px;
            vertical-align: middle;
          }
          .numeric-cell {
            text-align: right;
            font-family: 'Courier New', monospace;
            font-weight: 500;
            color: #2c3e50;
            font-size: 11px;
          }
          .footer-section {
            background-color: #f8f9fa;
            padding: 15px 20px;
            border-top: 1px solid #dee2e6;
            margin-top: 20px;
          }
          .footer-date {
            color: #666;
            font-size: 14px;
            margin: 0;
          }
          .footer-page {
            color: #666;
            font-size: 14px;
            text-align: right;
            margin: 0;
          }
          .table-responsive {
            overflow-x: auto;
            max-width: 100%;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .table {
            margin-bottom: 0;
            border-radius: 8px;
            overflow: hidden;
          }
          .table thead th:first-child {
            border-top-left-radius: 8px;
          }
          .table thead th:last-child {
            border-top-right-radius: 8px;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
              font-size: 10px;
              line-height: 1.2;
            }
            .container-fluid {
              padding: 0 !important;
              margin: 0 !important;
            }
            .card {
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
            }
            .card-body {
              padding: 5px !important;
            }
            .btn {
              display: none !important;
            }
            .table {
              border-collapse: collapse !important;
              width: 100% !important;
              font-size: 8px !important;
              table-layout: fixed !important;
            }
            .table th, .table td {
              border: 1px solid #000 !important;
              padding: 4px 2px !important;
              font-size: 8px !important;
              white-space: nowrap !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }
            .table th:nth-child(1), .table td:nth-child(1) { width: 45% !important; }
            .table th:nth-child(2), .table td:nth-child(2) { width: 10% !important; }
            .table th:nth-child(3), .table td:nth-child(3) { width: 12% !important; }
            .table th:nth-child(4), .table td:nth-child(4) { width: 16.5% !important; }
            .table th:nth-child(5), .table td:nth-child(5) { width: 16.5% !important; }
            .table th {
              background-color: #f8f9fa !important;
              font-weight: bold !important;
              text-align: center !important;
            }
            .table td {
              text-align: center !important;
            }
            .item-name {
              text-align: left !important;
            }
            .block-cell {
              text-align: center !important;
            }
            .item-code {
              text-align: center !important;
            }
            .numeric-cell {
              text-align: right !important;
              font-family: 'Courier New', monospace !important;
            }
            @page {
              margin: 0.5in;
              size: A4 landscape;
            }
            hr {
              display: none !important;
            }
          }
        `}
      </style>
      <div className="container-fluid py-3">
        {/* Header Section */}
        <div className="balance-sheet-header">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="page-title">Balance Sheet Data Check</h1>
            </div>
            <div className="col-md-6 text-end">
              <p className="dispatch-number">DSL No: {blockInfo.dslNo || 'Not Available'}</p>
              <p className="dispatch-number">Survey Year: {blockInfo.surveyYear || 'Not Available'}</p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="card mx-3 mb-3">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '35%', minWidth: '200px' }}>Item</th>
                    <th style={{ width: '12.5%', minWidth: '60px' }}>Block</th>
                    <th style={{ width: '12.5%', minWidth: '80px' }}>Item Code</th>
                    <th style={{ width: '20%', minWidth: '100px' }}>Current Year</th>
                    <th style={{ width: '20%', minWidth: '100px' }}>Previous Year</th>
                  </tr>
                </thead>
                <tbody>
                  {blockDData.length > 0 ? (
                    blockDData.map((item, index) => {
                      // Parse blockItem to extract Block and Item Code
                      const parseBlockItem = (blockItem) => {
                        if (!blockItem) return { block: 'D', itemCode: '' };

                        // Handle formats like "Block-D, Item-7" or "Block-D,Item-7"
                        const match = blockItem.match(/Block-([A-Z]),\s*Item-(\d+)/i);
                        if (match) {
                          return {
                            block: match[1],
                            itemCode: match[2]
                          };
                        }

                        // Fallback: try to extract just the block letter
                        const blockMatch = blockItem.match(/Block-([A-Z])/i);
                        if (blockMatch) {
                          return {
                            block: blockMatch[1],
                            itemCode: ''
                          };
                        }

                        return { block: 'D', itemCode: '' };
                      };

                      const { block, itemCode } = parseBlockItem(item.blockItem);

                      return (
                        <tr key={index}>
                          <td className="item-name">{item.itemName || ''}</td>
                          <td className="block-cell">{block}</td>
                          <td className="item-code">{itemCode}</td>
                          <td className="numeric-cell">{item.currentYearValue ? Number(item.currentYearValue).toLocaleString() : ''}</td>
                          <td className="numeric-cell">{item.previousYearValue ? Number(item.previousYearValue).toLocaleString() : ''}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No data available for this DSL No. and Survey Year
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {/* <div className="row mt-3">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
          </div>

          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/validation')}
            >
              Cancel
            </button>
          </div>

          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/fixed-assets-data')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/validation')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
                type="button" 
                className="btn btn_orange mx-1"
                onClick={() => navigate('/fixed-assets-data')}
              >
                <i className="fa fa-angle-left me-1"></i>
                Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/check-pl-account')}
            >
              Next 
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default BalanceSheetDataCheck; 